import React, { useEffect, useState } from "react";
import { fetchCustIds, fetchSitesForCustId, fetchCustIdsCsv, fetchSitesForCustIdCsv } from "./api";
import IdCountsTable from "./IdCountsTable";
import Breadcrumb from "./Breadcrumb";
import CsvExportButton from "./CsvExportButton";
import Paginator from "../../shared/Paginator";

const PAGE_SIZE = 20;

// Mirror of SiteWiseTab: level 1 is cust_ids, level 2 (drilled into a
// cust_id) is the sites that customer has activity on, where a count cell
// opens the events popup.
function CustWiseTab({
  siteSearch,
  custSearch,
  dateRange,
  sort,
  order,
  onSortClick,
  customColumns,
  onRemoveCustomColumn,
  onLevelChange,
  onOpenModal,
  onOpenSearchModal,
}) {
  const [level, setLevel] = useState("list"); // "list" | "drill"
  const [selectedCustId, setSelectedCustId] = useState(null);
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onLevelChange(level);
  }, [level, onLevelChange]);

  const activeSearch = level === "list" ? custSearch : siteSearch;
  const customSearches = customColumns.length ? customColumns.join(",") : undefined;

  const commonParams = (searchArg, sortArg, orderArg) => ({
    search: searchArg || undefined,
    dateFrom: dateRange?.startDate || undefined,
    dateTo: dateRange?.endDate || undefined,
    sort: sortArg,
    order: orderArg,
    customSearches,
  });

  const load = async (pageArg, levelArg, custIdArg, searchArg, sortArg, orderArg) => {
    setLoading(true);
    try {
      const params = { page: pageArg + 1, ...commonParams(searchArg, sortArg, orderArg) };
      const res = levelArg === "list" ? await fetchCustIds(params) : await fetchSitesForCustId(custIdArg, params);
      if (res.code === 200) {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (err) {
      console.error(err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    load(0, level, selectedCustId, activeSearch, sort, order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, selectedCustId, activeSearch, sort, order, customSearches, dateRange?.startDate, dateRange?.endDate]);

  const handlePageClick = (e) => {
    setPage(e.selected);
    load(e.selected, level, selectedCustId, activeSearch, sort, order);
  };

  const handleDrill = (custId) => {
    setSelectedCustId(custId);
    setLevel("drill");
  };

  const handleBack = () => {
    setSelectedCustId(null);
    setLevel("list");
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const fetchCsv = () =>
    level === "list"
      ? fetchCustIdsCsv(commonParams(activeSearch, sort, order))
      : fetchSitesForCustIdCsv(selectedCustId, commonParams(activeSearch, sort, order));

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb rootLabel="Cust IDs" currentLabel={level === "drill" ? selectedCustId : null} onRoot={handleBack} />
        <CsvExportButton fetchRows={fetchCsv} filename={level === "list" ? "custids-report.csv" : "sites-report.csv"} />
      </div>

      {level === "list" ? (
        <IdCountsTable
          items={items}
          idField="cust_id"
          idHeader="Cust ID"
          startIndex={page * PAGE_SIZE}
          onRowClick={handleDrill}
          loading={loading}
          sort={sort}
          order={order}
          onSortClick={onSortClick}
          customColumns={customColumns}
          onRemoveCustomColumn={onRemoveCustomColumn}
        />
      ) : (
        <IdCountsTable
          items={items}
          idField="site_id"
          idHeader="Site ID"
          showBaseUrl
          startIndex={page * PAGE_SIZE}
          onCountClick={(siteId, eventName) => onOpenModal(siteId, selectedCustId, eventName)}
          loading={loading}
          sort={sort}
          order={order}
          onSortClick={onSortClick}
          customColumns={customColumns}
          onRemoveCustomColumn={onRemoveCustomColumn}
          onCustomCountClick={(siteId, term) => onOpenSearchModal(siteId, selectedCustId, term)}
        />
      )}

      {totalPages > 1 && (
        <div className="mt-4">
          <Paginator
            pageCount={totalPages}
            handlePageClick={handlePageClick}
            forcePage={page}
            totalItems={total}
            pageSize={PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
}

export default CustWiseTab;
